import { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { removeTagsFromText } from '@aytm/app/stats/page/components/lib/utils'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// styles
import style from '@aytm/app/stats/page/question-views/cjd/components/head/styles/Persona.module.scss'

const formatAmount = amount => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`
  }
  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K`
  }
  return amount.toString()
}

const parseTraits = (traitDescription, traits) => {
  if (!Array.isArray(traitDescription)) return []

  return traitDescription.reduce((acc, cur) => {
    const ts = cur.split(/_/)
    const key = ts.slice(0, -1).join('_').toLowerCase()
    const val = ts.at(-1)
    const tId = Number.isInteger(parseInt(val, 10)) ? parseInt(val, 10) - 1 : val

    const trait = traits.find(t => (key === 'geo.region' && t.id === 'region') || key === t.id)

    if (!trait) return acc

    const name = trait.title
    const value = trait.values[tId] || trait.values.find(v => String(tId).toLowerCase() === String(v.id).toLowerCase())

    const i = acc.findIndex(v => v.name === name)
    if (i >= 0) {
      acc[i].value = `${acc[i].value}, ${value?.name || tId}`
    } else {
      acc.push({
        name,
        value: value?.name || tId
      })
    }
    return acc
  }, [])
}

const parseAnswers = (surveyDescription, questions) => {
  if (!Array.isArray(surveyDescription)) return []

  const regs = [
    /^Q(\d+)_(\d+)$/,
    /^Q(\d+)A(\d+)_([01])$/,
    /^Q(\d+)SQ(\d+)_(\d+)$/,
    /^Q(\d+)SQ(\d+)_(\d+)$/,
    /^Q(\d+)SQ(\d+)A(\d+)_(\d+)$/,
    /^Q(\d+)QV(\d+)SQ(\d+)_(\d+)$/
  ]

  return surveyDescription.map(a => {
    const regIndex = regs.findIndex(r => r.test(a))
    let link = a
    let value = a

    if (regIndex >= 0) {
      const m = value.match(regs[regIndex])
      if (m) {
        const q = questions?.find(v => v.position.toString() === m[1])
        if (q) {
          const qk = q.pre_type ? 'PQ' : 'Q'
          const qp = q.position
          const qvp = q.visual_position

          switch (regIndex) {
            case 0:
              link = `q-${qp}`
              value = `${qk}${qvp}A${m[2]}`
              break
            case 1:
              link = `q-${qp}`
              value = `${qk}${qvp}A${m[2]}`
              break
            case 2:
            case 3:
              link = `q-${qp}-b-${m[2]}`
              value = `${qk}${qvp}SQ${m[2]}A${m[3]}`
              break
            case 4:
              link = `q-${qp}-b-${m[2]}`
              value = `${qk}${qvp}SQ${m[2]}A${m[3]}`
              break
            case 5:
              link = `q-${qp}`
              value = `${qk}${qvp}QV${m[2]}SQ${m[3]}A${m[4]}`
              break
          }
        }
      }
    }

    return { link, value }
  })
}

const Persona = ({ traits, questions, answer, persona, visible, onScrollToQuestion }) => {
  const personaData = useMemo(() => {
    if (!persona || persona <= 0 || !answer) return null

    const cluster = answer.clusters?.[persona - 1]
    const assetId = String(
      Number.isInteger(answer.traitImageAssignment?.[persona - 1]) ? answer.traitImageAssignment[persona - 1] : 1
    ).padStart(4, '0')

    return {
      assetId,
      name: answer.clusterNames?.[persona - 1] || '',
      respondents: {
        amount: cluster?.amount || 0,
        total: cluster?.total || 0
      },
      description: answer.clusterDescriptions?.[persona - 1] || '',
      traits: parseTraits(answer.traitDescription?.[persona - 1], traits || []),
      answers: parseAnswers(answer.surveyDescription?.[persona - 1], questions)
    }
  }, [answer, persona, traits, questions])

  const handleAnswerClick = useCallback(
    (e, link) => {
      e.preventDefault()

      if (onScrollToQuestion) {
        const ops = link.split('-')
        onScrollToQuestion(`${ops[0]}${ops[1]}`, ops[3] || null)
      }
    },
    [onScrollToQuestion]
  )

  if (!personaData || persona <= 0) {
    return null
  }

  const { assetId, name, respondents, description, traits: personaTraits, answers } = personaData
  const cleanDescription = removeTagsFromText(description)

  return (
    <div className={classNames(style.persona, { [style.hidden]: !visible })}>
      <div className={style.photo}>
        <img src={`/images/stats/cjd/personas/${assetId}.jpg`} alt={name} />
      </div>
      <div className={style.data}>
        <div className={style.name}>
          <b>{name}</b>
          <Popup
            width="300px"
            trigger="click"
            direction="righttop"
            popupOffset={9}
            hideOtherPopupsOnShow
            content={() => (
              <>
                Hi there, my name is {name}. I'm an auto-generated customer persona created by this Conjoint
                Segmentation test. Respondents clustered with me may have different traits from mine, but they all
                expressed similar package preferences. The demographics used to describe me differentiate my persona
                cluster from other personas with 95% statistical significance.
              </>
            )}
          >
            <i className="icon-aytmfd-help-v2" />
          </Popup>
          <span>
            {respondents.amount === respondents.total ? (
              formatAmount(respondents.total)
            ) : (
              <>
                {formatAmount(respondents.amount)}/{formatAmount(respondents.total)} filtered
              </>
            )}{' '}
            respondents in this cluster
          </span>
        </div>

        {cleanDescription ? (
          <div className={style.description} dangerouslySetInnerHTML={{ __html: cleanDescription }} />
        ) : null}

        {personaTraits.length > 0 || answers.length > 0 ? (
          <div className={style.info}>
            {personaTraits.length > 0 ? (
              <div className={style.traits}>
                <div className={style.header}>Prevalent traits:</div>
                <div className={style.items}>
                  {personaTraits.map((trait, i) => (
                    <div key={i} className={style.item}>
                      {trait.name}: {trait.value}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {answers.length > 0 ? (
              <div className={style.answers}>
                <div className={style.header}>Favorite answers:</div>
                <div className={style.items}>
                  {answers.map((answer, i) => (
                    <div key={i} className={style.item}>
                      <a href="#" data-id={answer.link} onClick={e => handleAnswerClick(e, answer.link)}>
                        {answer.value}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

Persona.propTypes = {
  traits: PropTypes.array,
  questions: PropTypes.array,
  answer: answerPropType,
  persona: PropTypes.number,
  visible: PropTypes.bool,
  onScrollToQuestion: PropTypes.func
}

Persona.displayName = 'ConjointPersona'

export default Persona
